import {
  Button,
  Distribute,
  Logo as LoomLogo,
  Spacer,
  TextButton,
  TextInput,
} from '@loomhq/lens';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { updateAnonymousUsername } from 'actions/shareVideo';
import {
  ROLLOUT_AT_MENTION_COMMENT,
  ROLLOUT_SLACK_SHARED_AUTH_PUBLIC,
} from 'constants/featureFlag';
import { ENTER, ESCAPE } from 'constants/keyCodes';
import { RECORD_REPLY_SOURCE_COMMENT_BAR } from 'constants/sdk';
import { addCommentCreator } from 'creators/commentsGraphQl';
import { selectHasAuthenticatedAccessScope } from 'selectors/scopes';
import { selectUserIsLogged } from 'selectors/user';
import {
  selectCurrentVideoId,
  selectIsFromSlackDesktopApp,
} from 'selectors/videoPlayer';
import { getGraphQLClient } from 'utilities/graphql';
import { invokeSharedAuthFlow } from 'utilities/slack/sharedAuthHandler';

import useAnonymousUsername from 'hooks/useAnonymousUsername';
import { useFlagIsActivated } from 'hooks/featureFlag';
import { useGetSelectedWorkspace } from 'hooks/workspace';

import searchWorkspaceMembers from './searchWorkspaceMembers.gql';

import FeatureFlaggedComponent from 'components/feature-flag-wrapper';
import AutoresizeMentionTextarea from 'components/ui-components/autoresize-mention-textarea';
import AutoresizeTextarea from 'components/ui-components/autoresize-textarea';
import { RecordReplyWrapperMemo } from 'components/video-player/end-screen/record-reply-fallback-button';

import styles from './styles.module.less';

const ONELINE_MAX_ROWS = 4;
const MULTILINE_MIN_ROWS = 2;

const CommentInputGroup = props => {
  const dispatch = useDispatch();

  const anonUserName = useAnonymousUsername();
  const setAnonUserName = username =>
    dispatch(updateAnonymousUsername(username));

  const [comment, setComment] = useState('');
  const [elementToFocus, setFocus] = useState(null);

  const {
    addReplyId,
    allowOneLine,
    creationMethod,
    drawCancel,
    grabFocus,
    expandOnEntry,
    hideNameIfPresent,
    inputSize,
    onCancel,
    onSubmit,
    placeholder,
    submitButtonText,
    isCommentBar,
  } = props;

  const commentInput = useRef();
  const nameInput = useRef();

  const selectedWorkspace = useGetSelectedWorkspace();

  useEffect(() => {
    if (grabFocus && commentInput.current) {
      (elementToFocus || commentInput.current).focus({ preventScroll: true });
    }
  }, [grabFocus, elementToFocus]);

  const searchWorkspaceMembersFn = async query => {
    const gqlClient = getGraphQLClient();

    const result = await gqlClient.query({
      query: searchWorkspaceMembers,
      variables: {
        workspaceId: selectedWorkspace.id,
        query,
        limit: 10,
      },
    });

    return result.data.searchWorkspaceMembers.users
      .filter(user => user)
      .map(user => ({
        id: `user:${user.id}`,
        display: user.display_name,
        avatar: user.avatars[0]?.thumb,
      }));
  };

  const onAddComment = () => {
    dispatch(
      addCommentCreator({ comment, addReplyId, anonUserName, creationMethod })
    );

    commentInput.current.blur();

    setComment('');
    onSubmit && onSubmit();
  };

  const isFromSlackDesktopApp = useSelector(selectIsFromSlackDesktopApp);
  const isMainVideoOnSharePage = useSelector(
    state => state.video_player.isMainVideoOnSharePage
  );
  const videoId = useSelector(selectCurrentVideoId);

  const isLoggedIn = useSelector(selectUserIsLogged);
  const hasAuthenticatedAccess = useSelector(selectHasAuthenticatedAccessScope);
  const shouldBeAnon = !isLoggedIn || !hasAuthenticatedAccess;

  const direction = allowOneLine
    ? ['column', 'column', 'row', 'row']
    : 'column';

  function validCommentInput() {
    return Boolean(comment.length && comment.trim().length);
  }

  function canSubmitComment() {
    const needAnonName = shouldBeAnon && !anonUserName?.trim().length;

    return validCommentInput() && !needAnonName;
  }

  const showAllFields = !expandOnEntry || validCommentInput();
  const showActionButtons = showAllFields;
  const showAnonNameField =
    shouldBeAnon && !(hideNameIfPresent && anonUserName) && showAllFields;

  const isSlackSharedAuthEnabled = useFlagIsActivated({
    flag: ROLLOUT_SLACK_SHARED_AUTH_PUBLIC,
    activationValues: [true],
  });

  const isMentionComponentEnabled = useFlagIsActivated({
    flag: ROLLOUT_AT_MENTION_COMMENT,
    activationValues: [true],
  });

  const onKeyDown = e => {
    if (canSubmitComment() && e.keyCode === ENTER && !e.shiftKey) {
      onAddComment();
      e.preventDefault();
    } else if (e.keyCode === ESCAPE) {
      onCancel && onCancel();
    }
  };

  return (
    <Distribute
      className={styles.commentGroupContainer}
      direction={direction}
      alignment="end"
      gap="small"
    >
      <div
        className={classNames(styles.commentWrapper, {
          [styles.oneLineCommentWrapper]: allowOneLine && !shouldBeAnon,
          [styles.oneLineGuestCommentWrapper]: allowOneLine && shouldBeAnon,
        })}
      >
        {isMentionComponentEnabled ? (
          <AutoresizeMentionTextarea
            data-testid="comment-entry-field"
            size={inputSize}
            value={comment}
            placeholder={placeholder}
            onChange={ev => setComment(ev.target.value)}
            textRef={commentInput}
            onFocus={() => setFocus(commentInput.current)}
            minRows={!allowOneLine ? MULTILINE_MIN_ROWS : undefined}
            maxRows={allowOneLine ? ONELINE_MAX_ROWS : undefined}
            fetch={searchWorkspaceMembersFn}
            onKeyDown={onKeyDown}
          />
        ) : (
          <AutoresizeTextarea
            data-testid="comment-entry-field"
            size={inputSize}
            className={styles.textArea}
            value={comment}
            placeholder={placeholder}
            onChange={ev => setComment(ev.target.value)}
            textRef={commentInput}
            onFocus={() => setFocus(commentInput.current)}
            minRows={!allowOneLine ? MULTILINE_MIN_ROWS : undefined}
            maxRows={allowOneLine ? ONELINE_MAX_ROWS : undefined}
            onKeyDown={onKeyDown}
          />
        )}
      </div>
      {showAnonNameField && (
        <div
          className={classNames(styles.nameWrapper, {
            [styles.oneLineNameWrapper]: allowOneLine,
            [styles.oneLineNameWrapperWithSharedAuth]:
              isSlackSharedAuthEnabled && isFromSlackDesktopApp,
          })}
        >
          <TextInput
            data-testid="comment-anon-name-field"
            size={inputSize}
            placeholder="Your name"
            ref={nameInput}
            value={anonUserName}
            onFocus={() => setFocus(nameInput.current)}
            onChange={ev => setAnonUserName(ev.target.value)}
            onKeyDown={onKeyDown}
          />
          {isFromSlackDesktopApp && (
            <FeatureFlaggedComponent
              flag={ROLLOUT_SLACK_SHARED_AUTH_PUBLIC}
              activationValues={[true]}
            >
              <div
                className={classNames(styles.sharedAuthButton, {
                  [styles.sharedAuthButtonCollapsed]: anonUserName !== '',
                })}
              >
                <TextButton
                  size="small"
                  iconPosition="left"
                  icon={<LoomLogo variant="symbol" maxWidth={3} />}
                  onClick={() =>
                    invokeSharedAuthFlow(videoId, 'public comment', true)
                  }
                >
                  <p>Connect using Loom</p>
                </TextButton>
              </div>
            </FeatureFlaggedComponent>
          )}
        </div>
      )}
      {showActionButtons && (
        <div
          className={classNames(styles.actionButtonGroup, {
            [styles.allowReverse]: allowOneLine,
          })}
        >
          {drawCancel && (
            <TextButton size={inputSize} onClick={onCancel}>
              Cancel
            </TextButton>
          )}

          {!isCommentBar && isMainVideoOnSharePage && (
            <RecordReplyWrapperMemo
              addReplyId={addReplyId}
              source={RECORD_REPLY_SOURCE_COMMENT_BAR}
            />
          )}

          <Spacer left="small" />
          <Button
            size={inputSize}
            variant="primary"
            isDisabled={!canSubmitComment()}
            onClick={onAddComment}
          >
            {submitButtonText}
          </Button>
        </div>
      )}
      {!showActionButtons && <Spacer top="36px" />}
    </Distribute>
  );
};

export default CommentInputGroup;
